/*
 * "react-native" for the website. It is react-native-web with one change: Next.js turns an imported
 * image (require('@/assets/images/x.png')) into { src, width, height }, while React Native expects a
 * number or { uri }. These wrappers translate, so the app's screens stay exactly as they are.
 */
import React, { createElement, forwardRef, useEffect, useRef, useState } from 'react';
import { Image as WebImage, ImageBackground as WebImageBackground, StyleSheet as WebStyleSheet, View as WebView, Text as WebText, Pressable as WebPressable, ScrollView as WebScrollView, FlatList as WebFlatList, useWindowDimensions as useRealWindowDimensions, useColorScheme as useRealColorScheme } from 'react-native-web';

export * from 'react-native-web';

import { directFromProxy, optimizeImageUri, useCloudinarySource } from './cloudinary';

function normalize(source, style?) {
  if (source && typeof source === 'object' && !Array.isArray(source) && typeof source.src === 'string') {
    return { uri: source.src, width: source.width, height: source.height };
  }
  if (source && typeof source === 'object' && source.default && typeof source.default.src === 'string') {
    return { uri: source.default.src, width: source.default.width, height: source.default.height };
  }
  if (source && typeof source === 'object' && !Array.isArray(source) && typeof source.uri === 'string') {
    return { ...source, uri: optimizeImageUri(source.uri, style) };
  }
  return source;
}

/** resizeMode is a prop on the web, not a style. */
function pullResizeMode(props) {
  const flat = props.style ? WebStyleSheet.flatten(props.style) : null;
  if (flat && flat.resizeMode) {
    const { resizeMode, ...style } = flat;
    return { ...props, style, resizeMode: props.resizeMode ?? resizeMode };
  }
  return props;
}

export const Image: any = forwardRef<any, any>(function Image(props, ref) {
  const fixed = pullResizeMode(props);
  const sized = useCloudinarySource(fixed.source, fixed.style, fixed.onLayout);
  const resolved = normalize(sized.source, fixed.style);
  // If the photo cannot be fetched through salunbd.com/img, ask Cloudinary for it directly rather than show an empty box.
  const [failedUri, setFailedUri] = useState<string | null>(null);
  const direct = resolved && typeof resolved === 'object' && failedUri === resolved.uri ? directFromProxy(resolved.uri) : null;
  const finalSource = direct ? { ...resolved, uri: direct } : resolved;
  const onError = (event) => {
    if (resolved && typeof resolved === 'object' && directFromProxy(resolved.uri)) setFailedUri(resolved.uri);
    fixed.onError?.(event);
  };
  return <WebImage ref={ref} {...fixed} source={finalSource} onLayout={sized.onLayout} onError={onError} />;
});
Image.getSize = WebImage.getSize;
Image.prefetch = WebImage.prefetch;
Image.resolveAssetSource = (source) => normalize(source);

// A background picture fills its container, so it must not carry the file's own pixel size: on a window
// wider than the file, react-native-web would stop the picture at that size and leave the rest empty.
function withoutSize(source: any, style?: any) {
  const plain = normalize(source, style);
  return plain && typeof plain === 'object' && 'uri' in plain ? { uri: plain.uri } : plain;
}

export const ImageBackground = forwardRef<any, any>(function ImageBackground(props, ref) {
  const fixed = pullResizeMode(props);
  // The picture's own style lives in imageStyle.
  const image = fixed.imageStyle ? WebStyleSheet.flatten(fixed.imageStyle) : null;
  const plain = withoutSize(fixed.source, [fixed.style, image ?? fixed.imageStyle]);
  // If the photo cannot be fetched through salunbd.com/img, ask Cloudinary for it directly rather than show an empty box.
  const [failedUri, setFailedUri] = useState<string | null>(null);
  const direct = plain && typeof plain === 'object' && failedUri === plain.uri ? directFromProxy(plain.uri) : null;
  const source = direct ? { uri: direct } : plain;
  const onError = (event) => {
    if (plain && typeof plain === 'object' && directFromProxy(plain.uri)) setFailedUri(plain.uri);
    fixed.onError?.(event);
  };
  if (image && image.resizeMode) {
    const { resizeMode, ...imageStyle } = image;
    return <WebImageBackground ref={ref} {...fixed} imageStyle={imageStyle} resizeMode={fixed.resizeMode ?? resizeMode} source={source} onError={onError} />;
  }
  return <WebImageBackground ref={ref} {...fixed} source={source} onError={onError} />;
});

// The one thing React Native's own entry point has that react-native-web does not export by name.
export const LogBox = { ignoreLogs() {}, ignoreAllLogs() {}, install() {}, uninstall() {} };

// Native-module registry that React Native exposes and react-native-web leaves out; the app only probes it.
export const TurboModuleRegistry = { get: () => null, getEnforcing: () => { throw new Error('Native modules are not available on the web.'); } };

// The page is built on a server (which has no window) and then hydrated in the browser. React does
// not repair class names that differ between the two, so anything sized from the window would stay
// wrong. This hook gives a phone-sized window to the server and to the browser's first render, then
// the real one a moment later, which React does apply.
const SERVER_WINDOW = { width: 390, height: 844, scale: 1, fontScale: 1 };
export function useWindowDimensions() {
  const real = useRealWindowDimensions();
  const [mounted, setMounted] = useState(false);
  useEffect(() => setMounted(true), []);
  return mounted ? real : SERVER_WINDOW;
}

// Same idea for light and dark mode: the server renders light, so the browser's first render does too,
// and the visitor's real setting arrives a moment later.
export function useColorScheme() {
  const real = useRealColorScheme();
  const [mounted, setMounted] = useState(false);
  useEffect(() => setMounted(true), []);
  return mounted ? real : 'light';
}

// ---- props the app writes the phone way, translated to what the browser engine wants ---------------------

/** iOS-style shadow props become a CSS box-shadow, which is how the web draws shadows. */
function toBoxShadow(style) {
  if (!style || typeof style !== 'object' || Array.isArray(style)) return style;
  if (!('shadowColor' in style || 'shadowOpacity' in style || 'shadowRadius' in style || 'shadowOffset' in style)) return style;
  const { shadowColor = '#000000', shadowOpacity = 1, shadowRadius = 0, shadowOffset = { width: 0, height: 0 }, ...rest } = style;
  let color = shadowColor;
  const hex = /^#([0-9a-f]{3}|[0-9a-f]{6})$/i.exec(shadowColor);
  if (hex) {
    const h = hex[1].length === 3 ? hex[1].split('').map((c) => c + c).join('') : hex[1];
    color = `rgba(${parseInt(h.slice(0, 2), 16)}, ${parseInt(h.slice(2, 4), 16)}, ${parseInt(h.slice(4, 6), 16)}, ${shadowOpacity})`;
  } else if (shadowOpacity < 1) {
    color = `color-mix(in srgb, ${shadowColor} ${Math.round(shadowOpacity * 100)}%, transparent)`;
  }
  return { ...rest, boxShadow: `${shadowOffset.width || 0}px ${shadowOffset.height || 0}px ${shadowRadius}px ${color}` };
}

export const StyleSheet = {
  ...WebStyleSheet,
  create(styles) {
    const converted = {};
    for (const [name, style] of Object.entries(styles)) converted[name] = toBoxShadow(style);
    return WebStyleSheet.create(converted);
  },
};

/** pointerEvents is a style on the web, not a prop. */
function withPointerEvents(Component, displayName) {
  const Wrapped = forwardRef<any, any>(function Wrapped({ pointerEvents, style, ...rest }, ref) {
    return <Component ref={ref} {...rest} style={pointerEvents ? [style, { pointerEvents }] : style} />;
  });
  Wrapped.displayName = displayName;
  return Wrapped;
}
// `{value && <X />}` with an empty-string `value` renders "" as a direct child of a View. React Native
// ignores it; react-native-web logs "Unexpected text node" for every one. Drop empty strings here.
const BaseView = withPointerEvents(WebView, 'View');
export const View = forwardRef<any, any>(function View({ children, ...rest }, ref) {
  // Spread as separate children (not one array) so React does not ask for keys on static JSX.
  if (Array.isArray(children)) return createElement(BaseView, { ref, ...rest }, ...children.map((child) => (child === '' ? null : child)));
  return <BaseView ref={ref} {...rest}>{children === '' ? null : children}</BaseView>;
});
View.displayName = 'View';
export const Text = withPointerEvents(WebText, 'Text');
export const Pressable = withPointerEvents(WebPressable, 'Pressable');

/**
 * A horizontal list can be moved with a finger on a phone, but a mouse has no way to scroll sideways unless the
 * visitor knows to hold Shift. This lets a mouse drag the list instead (press, move left or right, let go), and
 * keeps the press that ends a drag from opening the card it finished on.
 */
function attachDragScroll(node: HTMLElement): () => void {
  let startX = 0;
  let startLeft = 0;
  let dragging = false;
  let moved = false;

  const onMove = (e: MouseEvent) => {
    const dx = e.clientX - startX;
    if (!moved && Math.abs(dx) > 5) {
      moved = true;
      node.style.cursor = 'grabbing';
      node.style.userSelect = 'none';
    }
    if (moved) node.scrollLeft = startLeft - dx;
  };
  const onUp = () => {
    window.removeEventListener('mousemove', onMove);
    window.removeEventListener('mouseup', onUp);
    node.style.cursor = '';
    node.style.userSelect = '';
    dragging = false;
  };
  const onDown = (e: MouseEvent) => {
    if (e.button !== 0 || node.scrollWidth <= node.clientWidth) return;
    startX = e.clientX;
    startLeft = node.scrollLeft;
    moved = false;
    dragging = true;
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  };
  // A drag ends with a click on whatever is under the mouse; swallow that one.
  const onClick = (e: MouseEvent) => {
    if (moved) {
      e.stopPropagation();
      e.preventDefault();
      moved = false;
    }
  };

  node.addEventListener('mousedown', onDown);
  node.addEventListener('click', onClick, true);
  node.style.cursor = 'grab';
  return () => {
    node.removeEventListener('mousedown', onDown);
    node.removeEventListener('click', onClick, true);
    node.style.cursor = '';
    if (dragging) onUp();
  };
}

function withDragScroll(Component, displayName) {
  const Wrapped = forwardRef<any, any>(function Wrapped(props, outerRef) {
    const inner = useRef<any>(null);
    useEffect(() => {
      if (!props.horizontal) return undefined;
      const node = inner.current?.getScrollableNode?.();
      return node ? attachDragScroll(node) : undefined;
    }, [props.horizontal]);
    return (
      <Component
        {...props}
        ref={(instance) => {
          inner.current = instance;
          if (typeof outerRef === 'function') outerRef(instance);
          else if (outerRef) outerRef.current = instance;
        }}
      />
    );
  });
  Wrapped.displayName = displayName;
  return Wrapped;
}
export const ScrollView = withDragScroll(WebScrollView, 'ScrollView');
export const FlatList = withDragScroll(WebFlatList, 'FlatList');
